import React, { useState, useEffect } from 'react';
import { forwardRef } from 'react';
import './JobForm.css';
import { API_BASE_URL } from '../config/apiConfig';
//import job from '../../../server/models/job';
//import JobDescriptionEditor from './JobDescriptionEditor'; // Rich text editor for job descriptions
//import JobPreviewBlock from './JobPreviewBlock'; // Optional preview component before publishing

// 🧩 Using forwardRef for external access/control (e.g., scroll/focus from parent)
const JobForm = forwardRef(({ token, onJobPosted, editData, onCancelEdit }, ref) => {
  // 🧠 Form state initialized with empty job fields
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    jobType: '', // Default job type
    qualification: '',
    applicationLink: '',
    notificationLink: '',
    officialLink: '',
    postedDate: '',
    startDate: '',
    lastDate: '',
    admitCardRelease: '',
    examDate: '',
    resultDate: '',
    pyqPaperLink: '', // Link for previous year question papers
    description: '' // 📝 Rich text content
  });

  // 🔔 UI feedback states
  const [message, setMessage] = useState('');
  const [toast, setToast] = useState('');
  //const [showPreview, setShowPreview] = useState(false); // 👀 Preview toggle

  // 🔄 Load data into form if editing existing job
  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || '',
        department: editData.department || '',
        location: editData.location || '',
        jobType: editData.jobType || '',
        qualification: editData.qualification || '',
        applicationLink: editData.applicationLink || '',
        notificationLink: editData.notificationLink || '',
        officialLink: editData.officialLink || '',
        postedDate: editData.postedDate?.split('T')[0] || '',
        startDate: editData.startDate?.split('T')[0] || '',
        lastDate: editData.lastDate?.split('T')[0] || '',
        admitCardRelease: editData.admitCardRelease || '',
        examDate: editData.examDate || '',
        resultDate: editData.resultDate || '',
        pyqPaperLink: editData.pyqPaperLink || '',
        description: editData.description || ''
      });
    } else {
      setFormData({
        title: '',
        department: '',
        location: '',
        jobType: '',
        qualification: '',
        applicationLink: '',
        notificationLink: '',
        officialLink: '',
        postedDate: '',
        startDate: '',
        lastDate: '',
        admitCardRelease: '',
        examDate: '',
        resultDate: '',
        pyqPaperLink: '',
        description: ''
      });
    }
  }, [editData]);

  // ⚙️ Generic input change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 📤 Submit or update job post
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 🌐 Determine HTTP method and endpoint based on context (edit/create)
    const method = editData ? 'PUT' : 'POST';
    const url = editData
      ? `${API_BASE_URL}/api/jobs/${editData._id}`
      : `${API_BASE_URL}/api/jobs`;

    // 🔐 Ensure admin authentication is present
    if (!token) {
      setMessage('❌ Admin login required to submit jobs.');
      return;
    }

    // 📅 Default posted date if empty (avoids blank entry)
    if (!formData.postedDate) {
      const now = new Date().toISOString().split('T')[0];
      formData.postedDate = now;
    }

    try {
      // 📡 Send job data to backend
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      // 🎉 Show success toast and reset form
      if (res.ok) {
        const toastText = editData ? '✅ Job updated successfully' : '✅ Job posted successfully';
        setToast(toastText);
        setMessage(toastText);
        setTimeout(() => setToast(''), 3000);

        // 🧹 Clear form after submission
        setFormData({
          title: '',
          department: '',
          location: '',
          jobType: '',
          qualification: '',
          applicationLink: '',
          notificationLink: '',
          officialLink: '',
          postedDate: '',
          startDate: '',
          lastDate: '',
          admitCardRelease: '',
          examDate: '',
          resultDate: '',
          pyqPaperLink: '',
          description: ''
        });

        onJobPosted(); // 🔁 Trigger parent refresh
      } else {
        const errorRes = await res.json();
        throw new Error(errorRes.message || '❌ Failed to submit job');
      }
    } catch (err) {
      setMessage(err.message);
    }
  };

  return (
    <form ref={ref} className="job-form" onSubmit={handleSubmit}>
      <h2>{editData ? '✏️ Edit Job' : '🖊️ Post a New Job'}</h2>

      {/* 🔧 Basic job metadata inputs */}
      <label>📝 Job Title</label>
      <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="Job Title" required />
      <label>🏢 Department</label>
      <input type="text" name="department" value={formData.department} onChange={handleChange} placeholder="Department" required />
      <label>📍 Location</label>
      <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="Location" required />
      <label>💼 Job Type</label>
      <select name="jobType" value={formData.jobType} onChange={handleChange} required>
        <option value="">Select Job Type</option>
        <option value="Government">Government</option>
        <option value="PSU">PSU</option>
        <option value="Railways">Railways</option>
        <option value="Banking">Banking</option>
        <option value="Defence">Defence</option>
        <option value="Teaching">Teaching</option>
      </select>

      <label>🎓 Qualification</label>
      <input type="text" name="qualification" value={formData.qualification} onChange={handleChange} placeholder="Qualification" required />
      <label>🔗 Application Link</label>
      <input type="url" name="applicationLink" value={formData.applicationLink} onChange={handleChange} placeholder="Application Link" required />
      <label>📄 Notification Link (PDF)</label>
      <input type="url" name="notificationLink" value={formData.notificationLink} onChange={handleChange} placeholder="Notification Link (PDF)" required/>
      <label>🌐 Official Link</label>
      <input type="url" name="officialLink" value={formData.officialLink} onChange={handleChange} placeholder="Official Website Link" required />
      <label>📄 Previous Year Question Paper</label>
      <input type="url" name="pyqPaperLink" value={formData.pyqPaperLink} onChange={handleChange} placeholder="Previous Year Question Paper Link" />

      {/* 📅 Date selectors */}
      <label>📅 Posted Date</label>
      <input type="date" name="postedDate" value={formData.postedDate} onChange={handleChange} required />

      <label>🚀 Start Date</label>
      <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required />

      <label>📌 Last Date</label>
      <input type="date" name="lastDate" value={formData.lastDate} onChange={handleChange} required />

      <label>📝 Admit Card Release</label>
      <input type="text" name="admitCardRelease" value={formData.admitCardRelease} onChange={handleChange} placeholder="Admit Card Release Date" />
      <label>📅 Exam Date</label> 
      <input type="text" name="examDate" value={formData.examDate} onChange={handleChange} placeholder="Exam Date" />
      <label>📅 Result Date</label>
      <input type="text" name="resultDate" value={formData.resultDate} onChange={handleChange} placeholder="Result Date" />

      {/* 📝 Rich text editor for job description 
      <label>📝 Job Description</label>
      <JobDescriptionEditor
        initialContent={formData.description}
        token={token}
        onSave={(desc) => setFormData(prev => ({ ...prev, description: desc }))}
        showToolbar={true}
      />*/}

      {/* 👀 Preview block toggle 
      <button type="button" onClick={() => setShowPreview(!showPreview)}>
        {showPreview ? '🙈 Hide Preview' : '👀 Preview Before Publish'}
      </button>*/}

      {/* 🖼 Conditionally render job preview 
      {showPreview && <JobPreviewBlock data={formData} />}*/}

      {/* 🚀 Final submission button */}
      <button type="submit">{editData ? '💾 Update Job' : '📤 Submit Job'}</button>

      {/* 🧹 Cancel editing option */}
      {editData && (
        <button type="button" className="cancel-button" onClick={onCancelEdit}>❌ Cancel Edit</button>
      )}

      {/* 🔔 Toast and status messages */}
      {toast && <div className="toast">{toast}</div>}
      {message && <p className="message">{message}</p>}
    </form>
  );
});

export default JobForm;
